// Staff configuration — extracted from buildingConfig in Fase 1. Each role
// now has 2-3 sub-skill axes (FM-style, drastically simplified) plus a
// persistent avatar id. The legacy single `skill` field is KEPT as the mean
// of the sub-skills: every parity-critical formula still reads it, so old
// saves behave identically after migration.

import { Rng, defaultRng, rollInt, hashString } from '@/game/utils/rng'

export type StaffType =
  | 'receptionist' | 'coach' | 'maintenance' | 'barista'
  | 'doctor' | 'physio'                 // cuerpo médico (Fase 3)
  | 'psychologist' | 'nutritionist'     // rendimiento (Fase 5)

export type StaffCategory = 'operaciones' | 'deportivo' | 'medico'

export interface Staff {
  id: string
  type: StaffType
  name: string
  salary: number // per week
  /** Overall skill 1-100 — the mean of `skills`, kept for compatibility */
  skill: number
  /** Per-axis sub-skills, keys from STAFF_SKILL_DEFS[type] */
  skills: Record<string, number>
  happiness: number // 1-100
  hiredDay: number
  /** Portrait pool key, e.g. 'coach_03' → /assets/portraits/staff/coach_03.webp */
  avatarId: string
}

export interface StaffSkillDef {
  key: string
  label: string
}

export const STAFF_SKILL_DEFS: Record<StaffType, ReadonlyArray<StaffSkillDef>> = {
  coach: [
    { key: 'tecnica', label: 'Técnica' },
    { key: 'tactica', label: 'Táctica' },
    { key: 'motivacion', label: 'Motivación' },
  ],
  maintenance: [
    { key: 'rapidez', label: 'Rapidez' },
    { key: 'calidad', label: 'Calidad' },
  ],
  receptionist: [
    { key: 'atencion', label: 'Atención' },
    { key: 'ventas', label: 'Ventas' },
  ],
  barista: [
    { key: 'producto', label: 'Producto' },
    { key: 'carisma', label: 'Carisma' },
  ],
  doctor: [
    { key: 'diagnostico', label: 'Diagnóstico' },
    { key: 'tratamiento', label: 'Tratamiento' },
  ],
  physio: [
    { key: 'recuperacion', label: 'Recuperación' },
    { key: 'prevencion', label: 'Prevención' },
  ],
  psychologist: [
    { key: 'presion', label: 'Gestión de presión' },
    { key: 'moral', label: 'Moral' },
  ],
  nutritionist: [
    { key: 'dieta', label: 'Dieta' },
    { key: 'energia', label: 'Energía' },
  ],
}

export interface StaffDefinition {
  type: StaffType
  name: string
  emoji: string
  description: string
  baseSalary: number
  benefits: string[]
  /** Building needed to hire this staff (facility registry id) */
  requires?: string
  /** Feature id (clubLevelConfig FEATURES) that unlocks hiring this role */
  feature?: string
  /** How many of this role the club can employ */
  maxCount: number
  category: StaffCategory
}

export const STAFF_DEFINITIONS: Record<StaffType, StaffDefinition> = {
  receptionist: {
    type: 'receptionist',
    name: 'Recepcionista',
    emoji: '👩‍💼',
    description: 'Gestiona reservas y atención al cliente.',
    baseSalary: 300,
    benefits: ['+5% demanda del club', 'Atención al socio'],
    requires: 'reception',
    maxCount: 1,
    category: 'operaciones',
  },
  coach: {
    type: 'coach',
    name: 'Entrenador',
    emoji: '🏆',
    description: 'Desarrolla a los jugadores de la plantilla y da clases.',
    baseSalary: 500,
    benefits: ['Acelera la progresión de jugadores', 'Con táctica alta, saltos de nivel mayores'],
    maxCount: 2,
    category: 'deportivo',
  },
  maintenance: {
    type: 'maintenance',
    name: 'Técnico Mantenimiento',
    emoji: '🔧',
    description: 'Repara pistas y edificios automáticamente.',
    baseSalary: 350,
    benefits: ['Reparación pasiva semanal', 'Frena el deterioro'],
    maxCount: 2,
    category: 'operaciones',
  },
  barista: {
    type: 'barista',
    name: 'Barista',
    emoji: '☕',
    description: 'Atiende la cafetería.',
    baseSalary: 250,
    benefits: ['+50% ingresos cafetería'],
    requires: 'cafe',
    maxCount: 1,
    category: 'operaciones',
  },
  doctor: {
    type: 'doctor',
    name: 'Médico Deportivo',
    emoji: '🩺',
    description: 'Trata las lesiones de la plantilla y acelera las altas.',
    baseSalary: 600,
    benefits: ['Recuperación de lesiones más rápida'],
    requires: 'locker',
    feature: 'hire_doctor',
    maxCount: 1,
    category: 'medico',
  },
  physio: {
    type: 'physio',
    name: 'Fisioterapeuta',
    emoji: '💆',
    description: 'Previene lesiones y trabaja la recuperación de los jugadores.',
    baseSalary: 450,
    benefits: ['-40% riesgo de lesión (prevención élite)', 'Acelera la recuperación'],
    requires: 'locker',
    feature: 'hire_physio',
    maxCount: 1,
    category: 'medico',
  },
  psychologist: {
    type: 'psychologist',
    name: 'Psicólogo Deportivo',
    emoji: '🧠',
    description: 'Sostiene la cabeza de la plantilla: frena las malas rachas de forma.',
    baseSalary: 400,
    benefits: ['Suelo de forma en malas rachas', '+1 moral semanal a todo el personal'],
    feature: 'hire_psychologist',
    maxCount: 1,
    category: 'medico',
  },
  nutritionist: {
    type: 'nutritionist',
    name: 'Nutricionista',
    emoji: '🥗',
    description: 'Planes de alimentación: los jugadores recuperan fatiga más rápido.',
    baseSalary: 350,
    benefits: ['Recuperación de fatiga acelerada'],
    feature: 'hire_nutritionist',
    maxCount: 1,
    category: 'medico',
  },
}

// --- Generation -------------------------------------------------------------

const FIRST_NAMES = ['Carlos', 'María', 'Juan', 'Ana', 'Pedro', 'Laura', 'Miguel', 'Elena', 'David', 'Sara']
const LAST_NAMES = ['García', 'Martínez', 'López', 'Sánchez', 'González', 'Rodríguez', 'Fernández', 'Pérez']

export function generateStaffName(rng: Rng = defaultRng): string {
  const first = FIRST_NAMES[Math.floor(rng() * FIRST_NAMES.length)]
  const last = LAST_NAMES[Math.floor(rng() * LAST_NAMES.length)]
  return `${first} ${last}`
}

export function generateRandomSkill(rng: Rng = defaultRng): number {
  // Bell curve around 50-70
  return Math.floor(40 + rng() * 40 + rng() * 20)
}

const AVATAR_VARIANTS = 4

/** Deterministic portrait key from the staff id (stable across sessions). */
export function assignAvatarId(type: StaffType, id: string): string {
  const variant = (hashString(id) % AVATAR_VARIANTS) + 1
  return `${type}_${String(variant).padStart(2, '0')}`
}

/** Derive per-axis sub-skills around a known overall skill (used both when
 *  migrating legacy staff and when a seed skill is requested): a bounded
 *  jitter per axis, clamped to [1, 100]. Display/new-feature detail only —
 *  parity-critical formulas keep reading the mean `skill`. */
export function deriveSkills(type: StaffType, skill: number, rng: Rng = defaultRng): Record<string, number> {
  const defs = STAFF_SKILL_DEFS[type] ?? []
  const skills: Record<string, number> = {}
  for (const def of defs) {
    const jitter = rollInt(-8, 8, rng)
    skills[def.key] = Math.max(1, Math.min(100, skill + jitter))
  }
  return skills
}

/** Build a complete Staff member (hire flow, career seeding, tests). */
export function createStaff(
  type: StaffType,
  opts: { day: number; skill?: number; id?: string; rng?: Rng } = { day: 1 },
): Staff {
  const rng = opts.rng ?? defaultRng
  const def = STAFF_DEFINITIONS[type]
  const id = opts.id ?? crypto.randomUUID()
  const skill = opts.skill ?? generateRandomSkill(rng)
  return {
    id,
    type,
    name: generateStaffName(rng),
    salary: def.baseSalary,
    skill,
    skills: deriveSkills(type, skill, rng),
    happiness: 80,
    hiredDay: opts.day,
    avatarId: assignAvatarId(type, id),
  }
}
